/**
 * @fileoverview Drawer state of the student tracker: which of the four
 * drawers (deliverable, self-assessment, teacher assessment view, final
 * self-assessment) is open and for which lesson. The deliverable drawer
 * carries an `openToken` used as React `key`, so it remounts with fresh state.
 */

import { useCallback, useState } from 'react'
import type { StudentTrackerLesson } from '../types'

export interface LessonDrawerState {
  opened: boolean
  lessonId: string
  lessonTitle: string
  /** `YYYY-MM-DD` */
  lessonDate: string
  assessmentId: string | null
}

export interface DeliverableDrawerState {
  opened: boolean
  openToken: number
  /** The activity whose deliverable the drawer shows (one task per activity). */
  activityId: string
  lessonTitle: string
}

const CLOSED_LESSON: LessonDrawerState = {
  opened: false,
  lessonId: '',
  lessonTitle: '',
  lessonDate: '',
  assessmentId: null,
}
const CLOSED_DELIVERABLE: DeliverableDrawerState = { opened: false, openToken: 0, activityId: '', lessonTitle: '' }

const lessonState = (lesson: StudentTrackerLesson): LessonDrawerState => ({
  opened: true,
  lessonId: lesson.id,
  lessonTitle: lesson.title,
  lessonDate: lesson.date,
  assessmentId: lesson.assessmentId,
})

export function useStudentTrackerDrawers() {
  const [deliverable, setDeliverable] = useState(CLOSED_DELIVERABLE)
  const [selfAssessment, setSelfAssessment] = useState(CLOSED_LESSON)
  const [teacherAssessment, setTeacherAssessment] = useState(CLOSED_LESSON)
  const [finalOpened, setFinalOpened] = useState(false)

  /** Opens the drawer for one activity of the lesson (defaults to the first deliverable). */
  const openDeliverable = useCallback((lesson: StudentTrackerLesson, activityId?: string) => {
    const target = activityId ?? lesson.deliverableActivities[0]?.id
    if (!target) return
    setDeliverable(current => ({
      opened: true,
      openToken: current.openToken + 1,
      activityId: target,
      lessonTitle: lesson.title,
    }))
  }, [])
  const closeDeliverable = useCallback(() => setDeliverable(current => ({ ...current, opened: false })), [])

  const openSelfAssessment = useCallback((lesson: StudentTrackerLesson) => setSelfAssessment(lessonState(lesson)), [])
  const closeSelfAssessment = useCallback(() => setSelfAssessment(CLOSED_LESSON), [])

  const openTeacherAssessment = useCallback(
    (lesson: StudentTrackerLesson) => setTeacherAssessment(lessonState(lesson)),
    [],
  )
  const closeTeacherAssessment = useCallback(() => setTeacherAssessment(CLOSED_LESSON), [])

  const openFinal = useCallback(() => setFinalOpened(true), [])
  const closeFinal = useCallback(() => setFinalOpened(false), [])

  return {
    deliverable,
    openDeliverable,
    closeDeliverable,
    selfAssessment,
    openSelfAssessment,
    closeSelfAssessment,
    teacherAssessment,
    openTeacherAssessment,
    closeTeacherAssessment,
    finalOpened,
    openFinal,
    closeFinal,
  }
}

export type StudentTrackerDrawers = ReturnType<typeof useStudentTrackerDrawers>
